import axios from 'axios';
import { createApiClient } from '../../api/client';
import type { BalanceDashboard } from './types';

/**
 * un-custom-dashboard talks to the `undash` REST API
 * (`/api/v1/undash/balance-dashboard`). Contract source:
 * `flows/UN-custom-dashboard/API_CONTRACT.md` (authoritative — PRD §7).
 *
 * Auth: standard authenticated embed. The host-injected bearer JWT (carrying
 * the `CAN_SEE_CARD_BALANCE_DASHBOARD` authority + `user_id` claim) travels in
 * the `Authorization` header, attached by `createApiClient` — never in the
 * body. The user is identified from the token, so the request has no params.
 *
 * Base URL is `VITE_API_URL` from `.env.<mode>` — empty in dev / unit tests so
 * MSW (relative-path handlers) intercepts; per-env builds point it at the real
 * service.
 */
const API_BASE = import.meta.env.VITE_API_URL ?? '';
export const BALANCE_DASHBOARD_PATH = '/api/v1/undash/balance-dashboard';

const apiClient = () => createApiClient(API_BASE);

// A 200 with a non-numeric balance or empty currency would otherwise paint a
// confident "0.00"/"NaN" with no currency, so reject it as contract drift.
const isBalanceDashboard = (d: unknown): d is BalanceDashboard => {
  const b = d as Partial<BalanceDashboard> | null;
  return (
    !!b &&
    typeof b.availableBalance === 'number' &&
    Number.isFinite(b.availableBalance) &&
    typeof b.assetType === 'string' &&
    b.assetType.length > 0
  );
};

/** Fetch the authenticated employee's available card balance. */
export const fetchBalanceDashboard = async (): Promise<BalanceDashboard> => {
  const { data } = await apiClient().get<BalanceDashboard>(BALANCE_DASHBOARD_PATH);
  if (!isBalanceDashboard(data)) {
    throw new Error('Malformed balance-dashboard response');
  }
  return data;
};

// --- Error extraction ------------------------------------------------------

export type ApiError = {
  /** HTTP status (undefined for a transport/network error with no response). */
  status?: number;
  /** Top-level `errorCode` when present (e.g. `UNAUTHORIZED`, `FORBIDDEN`). */
  errorCode: string | null;
};

/** Both contract envelopes expose a top-level `errorCode` (PRD §7). */
type ErrorBody = { errorCode?: string };

/** Normalise an unknown thrown error into `{ status, errorCode }`. */
export const parseApiError = (error: unknown): ApiError => {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as ErrorBody | undefined;
    return { status: error.response?.status, errorCode: body?.errorCode ?? null };
  }
  return { errorCode: null };
};
